import { useState, useEffect } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const STORAGE_KEY = "cookie_consent_v1";
const EVENT_NAME = "cookies:reopen";

export function getCookieConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
}

// Programmatic reopen — used by CookiePolicy page and any "manage cookies" button.
export function openCookieBanner() {
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

export default function CookieBanner() {
  const [open, setOpen] = useState(() => !getCookieConsent());
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    const existing = getCookieConsent();
    return {
      necessary: true,
      analytics: !!existing?.analytics,
      preferences: !!existing?.preferences,
    };
  });

  // Reopen when the CookiePolicy page (or any component) dispatches the event.
  useEffect(() => {
    const handler = () => {
      const existing = getCookieConsent();
      setPrefs({
        necessary: true,
        analytics: !!existing?.analytics,
        preferences: !!existing?.preferences,
      });
      setShowDetails(true);
      setOpen(true);
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const save = (consent) => {
    const payload = {
      ...consent,
      necessary: true,
      decided_at: new Date().toISOString(),
      version: "2026.02",
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setOpen(false);
    // Ledger call: fire-and-forget so a network error never blocks the UX.
    api.post("/consents/record", {
      kind: "cookies",
      accepted: true,
      version: "2026.02",
      details: payload,
    }).catch(() => { /* silent — server-side ledger is best-effort */ });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[40] p-3 sm:p-6 pointer-events-none" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-border pointer-events-auto overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg font-semibold">Aviso de cookies</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Utilizamos cookies <strong>técnicas estrictamente necesarias</strong> para el funcionamiento
                del servicio (sesión, autenticación, idioma). Opcionalmente podemos usar cookies de análisis
                o preferencias para mejorar la experiencia. Consulta nuestra{" "}
                <Link to="/politica-cookies" className="text-primary underline">Política de Cookies</Link>{" "}
                y la <Link to="/privacidad" className="text-primary underline">Política de Privacidad</Link>.
              </p>
            </div>
            <button onClick={() => save({ analytics: false, preferences: false })} className="text-muted-foreground hover:text-foreground p-1" aria-label="Cerrar">
              <X className="w-4 h-4" />
            </button>
          </div>

          {showDetails && (
            <div className="space-y-2 mb-4 border-t pt-4" data-testid="cookie-details">
              {[
                { k: "necessary", t: "Necesarias", d: "Sesión, autenticación, idioma. No se pueden desactivar.", required: true },
                { k: "preferences", t: "Preferencias", d: "Recordar configuración de UI, idioma y consentimiento del chat con IA." },
                { k: "analytics", t: "Análisis", d: "Estadísticas anónimas para mejorar el servicio (actualmente sin herramientas de terceros)." },
              ].map((c) => (
                <label key={c.k} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/30 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prefs[c.k]}
                    disabled={c.required}
                    onChange={(e) => setPrefs((p) => ({ ...p, [c.k]: e.target.checked }))}
                    className="mt-0.5"
                    data-testid={`cookie-toggle-${c.k}`}
                  />
                  <div>
                    <div className="font-medium text-sm">{c.t} {c.required && <span className="text-xs text-muted-foreground">(siempre activas)</span>}</div>
                    <div className="text-xs text-muted-foreground">{c.d}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => save({ analytics: false, preferences: false })} data-testid="cookie-reject">
              Solo necesarias
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowDetails((s) => !s)} data-testid="cookie-customize">
              {showDetails ? "Ocultar" : "Personalizar"}
            </Button>
            <Button size="sm" onClick={() => save(showDetails ? prefs : { analytics: true, preferences: true })} className="bg-primary hover:bg-primary/90" data-testid="cookie-accept">
              {showDetails ? "Guardar selección" : "Aceptar todas"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
